import { forwardRef, useImperativeHandle, useMemo } from 'react'
import invariant from 'invariant'
import { useNavigate } from 'react-router-dom'

import { Button, NotificationDialog, Stack, Text } from '../../../components'
import * as SecuritiesPayment from '../../../components/payment/securities'
import { mutations } from '../../../graphql'
import { fromAmountField } from '../../../utils'

export const PaymentUI = forwardRef(({ register, control, formState, stepperState }, ref) => {
  // Read selectedPath from stepperState (set in step 1)
  const selectedPath = stepperState?.payment?.selectedPath

  useImperativeHandle(ref, () => ({
    selectedPath,
  }))

  // Path A: Donor works with a financial advisor - show simplified form
  if (selectedPath === SecuritiesPayment.SECURITIES_PATH.ADVISOR) {
    return (
      <Stack spacing={2}>
        <SecuritiesPayment.AdvisorDetailsForm {...{ register, control, formState }} hideIntendedUse />
      </Stack>
    )
  }

  // Path B: Self-directed - show simplified form
  return (
    <Stack spacing={2}>
      <SecuritiesPayment.SelfDirectedCustodianInfo {...{ register, formState }} />
      <SecuritiesPayment.SecuritiesToTransferInKind {...{ register, control, formState }} />
      <SecuritiesPayment.Acknowledgement {...{ control, formState }} />
    </Stack>
  )
})

export async function getPaymentValues({ values }) {
  return values
}

export function SuccessUI({ me, user, securities, payment, contributions, donationTargetName, donationTargetId }) {
  const isAdvisorPath = payment?.selectedPath === 'advisor'

  const letterOfDirectionPdf = useMemo(
    () =>
      isAdvisorPath
        ? null
        : SecuritiesPayment.fetchGeneratedLetterOfDirectionPdf({
            me,
            user,
            securities,
            deliveringCustodianInfo: payment,
            contributionIds: contributions.map((contribution) => contribution.id),
          }),
    [contributions, me, user, payment, securities, isAdvisorPath]
  )

  const navigate = useNavigate()
  const handleSignUpClick = () => {
    navigate('/sign-up')
  }

  const signUpPrompt = (
    <>
      <Text.H6 sx={{ mb: 1 }}>
        <strong>Make Giving Easier - Create Your Giving Fund Today</strong>
      </Text.H6>
      <Text.Body>If you haven&apos;t already, add a password and set up a Giving Fund to:</Text.Body>
      <ul>
        <li>
          <Text.Body>Instantly access a single tax receipt for all your donations, anytime.</Text.Body>
        </li>
        <li>
          <Text.Body>Set up recurring gifts with ease.</Text.Body>
        </li>
        <li>
          <Text.Body>Organize and manage all your donations in one convenient place.</Text.Body>
        </li>
      </ul>
    </>
  )

  // Path A: Advisor - show notification message
  if (isAdvisorPath) {
    return (
      <NotificationDialog
        open
        onClose={handleSignUpClick}
        type="success"
        message={
          <>
            <Text.Body sx={{ mb: 1 }}>
              Thank you for initiating this generous gift to {donationTargetName} ({donationTargetId}).
            </Text.Body>
            <Text.Body sx={{ mb: 3 }}>
              We have notified {payment?.advisorName} via email about your intention for this transaction and have
              encouraged them to reach out to you to coordinate the transfer.
            </Text.Body>
            {signUpPrompt}
          </>
        }
        actions={[{ label: 'Add Password', onClick: handleSignUpClick }]}
      />
    )
  }

  // Path B: Self-directed - show letter of direction
  return (
    <NotificationDialog
      open
      onClose={handleSignUpClick}
      type="success"
      title="One More Step! Action Required to Complete Donation."
      message={
        <>
          <Text.Body sx={{ mb: 1 }}>
            Thank you for your donation. Your generosity is making a meaningful impact, and we&apos;re so grateful for
            your support.
          </Text.Body>
          <Text.Body sx={{ mb: 3 }}>
            To complete this donation, you will now need to initiate the transfer of securities to GiveWise.
          </Text.Body>
          <Text.Body sx={{ mb: 2 }}>
            For your convenience, here is a link to download a{' '}
            <strong>&quot;Gift of Securities - Letter of Direction&quot;</strong> that can be shared with your
            brokerage.
          </Text.Body>

          {/* PDF Link Component */}
          <SecuritiesPayment.GeneratedLetterOfDirection letterOfDirectionPdf={letterOfDirectionPdf} />

          <Text.Body sx={{ mt: 3, mb: 3 }}>
            Once the security has been received and has liquidated, the gift will be sent and you will receive an email
            notification.
            <strong> NOTE: There may be adjustments due to fluctuations in value and exchange.</strong>
          </Text.Body>
          {signUpPrompt}
        </>
      }
      actions={[{ label: 'Add Password', onClick: handleSignUpClick }]}
    />
  )
}
